import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import { fetchGql } from '@/lib/graphql_client';
import { authFetch, AuthHttpError } from '@/lib/rest_client';
import { keycloak } from '@/lib/keycloak';
import { SwitchContextDocument } from '@/gql/graphql';
import { useContextStore, activeContextStorage } from './context-store';
import { normalizePersona } from './normalize-persona';
import type { ActiveContext } from './session.types';

type RestSwitchResponse = { tenantId: string; persona: string; displayName?: string | null };

interface SwitchContextOptions {
  /**
   * When true, the global isSwitching flag is NOT set for this call.
   * Use for boot-time bootstrap switches (ReadinessGate) where the Phase-1
   * screen already covers the UI — the WorkspaceSwitchOverlay must not fire.
   */
  suppressOverlay?: boolean;
}

/**
 * Switch the active context — `switchContext` GraphQL mutation (ENT-88 R3, T17).
 *
 * This is the ONLY way the active context is set: the mutation writes the
 * server-side session (keyed by the KC sid), which the gateway treats as the
 * single source of truth. On success we mirror the resolved `{tenantId, persona}`
 * into the store, persist the tenant id for reload restore, and RESET the entire
 * query cache (clear + refetch) so the previous context's data can never be shown
 * — not even for the moment between switching and the refetch resolving.
 * A `FORBIDDEN` denial (no membership) surfaces via the mutation `error` — the
 * store is NOT mutated (detect with {@link isNoMembershipError}).
 */
export function useSwitchContext(opts?: SwitchContextOptions) {
  const queryClient = useQueryClient();
  const setActive = useContextStore((s) => s.setActive);
  const setSwitching = useContextStore((s) => s.setSwitching);

  return useMutation({
    mutationKey: ['switchContext'],
    mutationFn: async (contextTenantId: string): Promise<ActiveContext> => {
      try {
        const { switchContext } = await fetchGql(SwitchContextDocument, { contextTenantId });
        return { tenantId: switchContext.tenantId, persona: normalizePersona(switchContext.persona) };
      } catch (err) {
        // Backend hasn't shipped switchContext yet — fall back to REST /auth/context
        const isValidation =
          err instanceof ClientError &&
          (err.response?.errors?.some((e) => e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') ?? false);
        if (!isValidation) throw err;

        try {
          const ctx = await authFetch<RestSwitchResponse>('/auth/context', {
            method: 'POST',
            body: { contextTenantId },
          });
          return { tenantId: ctx.tenantId, persona: normalizePersona(ctx.persona) };
        } catch (restErr) {
          // REST also unavailable — backend uses JWT token for routing anyway.
          // Set context locally from KC token claims so the gate unblocks.
          if (restErr instanceof AuthHttpError && restErr.status !== 404) throw restErr;
          const parsed = keycloak.tokenParsed as Record<string, unknown> | undefined;
          const roles = ((parsed?.realm_access as { roles?: string[] } | undefined)?.roles ?? []);
          const personaRole = roles.find((r) =>
            ['PLATFORM_ADMIN', 'AGENCY', 'CLIENT', 'VENDOR', 'WORKER'].includes(r),
          );
          return { tenantId: contextTenantId, persona: normalizePersona(personaRole ?? 'SA') };
        }
      }
    },
    // `isSwitching` drives the global progress bar + switcher spinner. It stays
    // true across the whole switch because onSuccess below is awaited before
    // onSettled — i.e. through the round-trip AND the post-switch refetch.
    // Boot-time switches pass suppressOverlay:true to skip this entirely.
    onMutate: () => { if (!opts?.suppressOverlay) setSwitching(true); },
    onSuccess: async (active) => {
      setActive(active);
      activeContextStorage.set(active.tenantId);
      // Reset ALL context-scoped queries (active + inactive): clears the previous
      // context's data so it's never visible, then refetches active ones against
      // the new server session. Unlike invalidateQueries (which keeps stale data
      // on screen while refetching), reset drops it to loading state. Awaited so
      // the indicator spans the reload, not just the round-trip. The user-scoped
      // contexts list (the switcher's own data) is preserved — identical across
      // contexts, so resetting it would only flicker the open switcher.
      await queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== 'contexts' });
    },
    onSettled: () => { if (!opts?.suppressOverlay) setSwitching(false); },
  });
}

/** A `switchContext` denial — GraphQL error with `extensions.code === 'FORBIDDEN'`. */
export const isNoMembershipError = (e: unknown): boolean =>
  e instanceof ClientError &&
  (e.response?.errors?.some((x) => x.extensions?.code === 'FORBIDDEN') ?? false);
